import React, { useState } from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';

import { Text, Title, Box } from '@nerdwallet/currency';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import {
  stringTextAreaSpec,
  boolCheckboxSpec,
  subtitleInput,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import parseStyleFormatting from '../../lib/parse-style-formatting';

import BackgroundColor from '../background-color/background-color';
import SmartButton from '../smart-button/smart-button';

import './last-chance.less';

const LastChance = ({
  title,
  subtitle,
  bgOptions,
  fontChronicle,

  // For SmartButton
  buttonURL,
  buttonText,
  textColorOptions,
  titleColorOptions,
  ...smartButtonProps
}) => {
  // Only append trk params if this is not an #anchor link on the
  // current page.
  //
  // @todo in the future we can extract this to a new maybeSetTrk()
  //     util if there are multiple use cases.
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL &&
      setTrk({
        url: buttonURL,
        params: {
          [TrkTag.location]: 'LastChance',
          [TrkTag.element]: TrkElement.button,
          [TrkTag.copy]: buttonText,
        },
      });

  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    id: `last-chance-${trkId}`,
    elementName: 'LastChance',
  };

  const { textColor } = getTextColor(textColorOptions);
  const colorStyle = { color: textColor };

  const { textColor: titleTextColor } = getTextColor(titleColorOptions);
  const titleColorStyle = { color: titleTextColor };

  return (
    <Impression eventType="element_impression" entityName="last_chance">
      <ElementImpression {...elementImpressionProps}>
        <BackgroundColor bgOptions={bgOptions}>
          {(backgroundColor) => (
            <div
              className="last-chance"
              style={{
                backgroundColor,
              }}
            >
              <Box
                className={classNames([
                  'last-chance__content',
                  'centered-in-parent',
                ])}
              >
                <Title
                  size="small"
                  className={classNames([
                    'last-chance__title',
                    {
                      gotham: !fontChronicle,
                    },
                  ])}
                  style={titleColorStyle}
                >
                  {parseStyleFormatting(title)}
                </Title>
                <Box style={colorStyle}>
                  <Text>{subtitle}</Text>
                </Box>
                <Box
                  className={classNames([
                    classes('padding-top-4'),
                    classes('padding-bottom-5--wide'),
                    classes('padding-bottom-4'),
                  ])}
                >
                  <SmartButton
                    buttonURL={modifiedUrl}
                    buttonText={buttonText}
                    buttonClass="last-chance__button"
                    {...smartButtonProps}
                  />
                </Box>
              </Box>
            </div>
          )}
        </BackgroundColor>
      </ElementImpression>
    </Impression>
  );
};

LastChance.displayName = 'LastChance';

LastChance.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  fontChronicle: PropTypes.bool,
  ...SmartButton.propTypes,
};

LastChance.defaultProps = {
  buttonType: 'custom',
  fontChronicle: false,
  ...BackgroundColor.defaultProps,
  ...SmartButton.defaultProps,
};

LastChance.getEditingConfig = (componentDocument) => {
  return [
    ...BackgroundColor.getEditingConfig(componentDocument),
    ...getTextColor.getEditingConfig(componentDocument),
    stringTextAreaSpec('title', 'Title', {
      help: 'For GothamBlack font, surround title text with | like |this|',
    }),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
    boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
    subtitleInput,
    ...SmartButton.getEditingConfig(componentDocument),
  ];
};

export default LastChance;
